import { ClassNameProps, Column, makeClassName, Paragraph, StyleProps } from "@hex-insights/core";
import {
	Table,
	TableBodyProps,
	TableCellProps,
	TableHeaderProps,
	TableHeadingProps,
	TableProps,
	TableRowProps,
} from "@hex-insights/tables";
import styles from "./table.module.css";

export function ReportCardDescriptionTable<C extends string>({ className, ...props }: TableProps<C>) {
	return <Table {...props} className={makeClassName(styles["table"], className)} />;
}

ReportCardDescriptionTable.Header = function ({ className, ...props }: TableHeaderProps) {
	return <Table.Header {...props} className={makeClassName(styles["table__header"], className)} />;
};

ReportCardDescriptionTable.Body = function ({ className, ...props }: TableBodyProps) {
	return <Table.Body {...props} className={makeClassName(styles["table__body"], className)} />;
};

ReportCardDescriptionTable.Row = function ({ className, ...props }: TableRowProps) {
	return <Table.Row {...props} className={makeClassName(styles["table__row"], className)} />;
};

ReportCardDescriptionTable.Heading = function <T extends string>({
	noResize = true,
	className,
	...props
}: TableHeadingProps<T>) {
	return (
		<Table.Heading noResize={noResize} {...props} className={makeClassName(styles["table__heading"], className)} />
	);
};

ReportCardDescriptionTable.Cell = function ({ className, ...props }: TableCellProps) {
	return <Table.Cell {...props} className={makeClassName(styles["table__cell"], className)} />;
};

const definitionListColumns = ["label", "body"];

const definitionListColumnWidths = {
	label: "12rem",
};

export type DefinitionListTableProps = {
	title: string;
	items: { label: string; body: string[] }[];
} & Partial<ClassNameProps & StyleProps>;

export function DefinitionListTable({ title, items, className, style }: DefinitionListTableProps) {
	return (
		<ReportCardDescriptionTable
			columns={definitionListColumns}
			columnWidths={definitionListColumnWidths}
			className={className}
			style={style}
		>
			<ReportCardDescriptionTable.Header>
				<ReportCardDescriptionTable.Row>
					<ReportCardDescriptionTable.Heading column={definitionListColumns[0]} colspan={definitionListColumns.length}>
						{title}
					</ReportCardDescriptionTable.Heading>
				</ReportCardDescriptionTable.Row>
			</ReportCardDescriptionTable.Header>
			<ReportCardDescriptionTable.Body>
				{items.map(({ label, body }, i) => (
					<ReportCardDescriptionTable.Row key={i}>
						<ReportCardDescriptionTable.Cell column="label" style={{ fontWeight: "bold" }}>
							<Column justify="center" style={{ height: "100%" }}>
								{label}
							</Column>
						</ReportCardDescriptionTable.Cell>
						<ReportCardDescriptionTable.Cell column="body">
							{body.map((text, i) => (
								<Paragraph key={i}>{text}</Paragraph>
							))}
						</ReportCardDescriptionTable.Cell>
					</ReportCardDescriptionTable.Row>
				))}
			</ReportCardDescriptionTable.Body>
		</ReportCardDescriptionTable>
	);
}

const igcseIBMarkKeyColumns = ["igcse", "ib"];

export type IGCSEIBMarkKeyTableProps = {
	items: { igcse: string; ib: string }[];
} & Partial<ClassNameProps & StyleProps>;

export function IGCSEIBMarkKeyTable({ items, className, style }: IGCSEIBMarkKeyTableProps) {
	return (
		<ReportCardDescriptionTable columns={igcseIBMarkKeyColumns} className={className} style={style}>
			<ReportCardDescriptionTable.Header>
				<ReportCardDescriptionTable.Row>
					<ReportCardDescriptionTable.Heading column="igcse">International GCSE</ReportCardDescriptionTable.Heading>
					<ReportCardDescriptionTable.Heading column="ib">IB</ReportCardDescriptionTable.Heading>
				</ReportCardDescriptionTable.Row>
			</ReportCardDescriptionTable.Header>
			<ReportCardDescriptionTable.Body>
				{items.map(({ igcse, ib }, i) => (
					<ReportCardDescriptionTable.Row key={i}>
						<ReportCardDescriptionTable.Cell
							column="igcse"
							style={{
								textAlign: "center",
								fontWeight: "bold",
							}}
						>
							{igcse}
						</ReportCardDescriptionTable.Cell>
						<ReportCardDescriptionTable.Cell column="ib" style={{ textAlign: "center" }}>
							{ib}
						</ReportCardDescriptionTable.Cell>
					</ReportCardDescriptionTable.Row>
				))}
			</ReportCardDescriptionTable.Body>
		</ReportCardDescriptionTable>
	);
}

const gradeDescriptorsColumns = ["grade", "description"];

const gradeDescriptorsColumnWidths = {
	grade: "10.5rem",
};

export type GradeDescriptorsTableProps = {
	items: { grade: string; description: string[] }[];
} & Partial<ClassNameProps & StyleProps>;

export function GradeDescriptorsTable({ items, className, style }: GradeDescriptorsTableProps) {
	return (
		<ReportCardDescriptionTable
			columns={gradeDescriptorsColumns}
			columnWidths={gradeDescriptorsColumnWidths}
			className={className}
			style={style}
		>
			<ReportCardDescriptionTable.Header>
				<ReportCardDescriptionTable.Row>
					<ReportCardDescriptionTable.Heading column="grade">Grade</ReportCardDescriptionTable.Heading>
					<ReportCardDescriptionTable.Heading column="description">Descriptor</ReportCardDescriptionTable.Heading>
				</ReportCardDescriptionTable.Row>
			</ReportCardDescriptionTable.Header>
			<ReportCardDescriptionTable.Body>
				{items.map(({ grade, description }, i) => (
					<ReportCardDescriptionTable.Row key={i}>
						<ReportCardDescriptionTable.Cell column="grade">
							<Column justify="center" align="center" style={{ height: "100%" }}>
								<strong>{grade}</strong>
							</Column>
						</ReportCardDescriptionTable.Cell>
						<ReportCardDescriptionTable.Cell column="description">
							{description.map((text, i) => (
								<Paragraph key={i}>{text}</Paragraph>
							))}
						</ReportCardDescriptionTable.Cell>
					</ReportCardDescriptionTable.Row>
				))}
			</ReportCardDescriptionTable.Body>
		</ReportCardDescriptionTable>
	);
}

const academicAchievementKeyColumns = ["grade", "boundaries", "description"];

const academicAchievementKeyColumnWidths = {
	grade: "10.5rem",
	boundaries: "10.5rem",
};

export type AcademicAchievementKeyTableProps = {
	items: { gradeValue: string; gradeLabel: string; boundaries: string; description: string[] }[];
} & Partial<ClassNameProps & StyleProps>;

export function AcademicAchievementKeyTable({ items, className, style }: AcademicAchievementKeyTableProps) {
	return (
		<ReportCardDescriptionTable
			columns={academicAchievementKeyColumns}
			columnWidths={academicAchievementKeyColumnWidths}
			className={className}
			style={style}
		>
			<ReportCardDescriptionTable.Header>
				<ReportCardDescriptionTable.Row>
					<ReportCardDescriptionTable.Heading column="grade">Grade</ReportCardDescriptionTable.Heading>
					<ReportCardDescriptionTable.Heading column="boundaries">Boundaries</ReportCardDescriptionTable.Heading>
					<ReportCardDescriptionTable.Heading column="description">Descriptor</ReportCardDescriptionTable.Heading>
				</ReportCardDescriptionTable.Row>
			</ReportCardDescriptionTable.Header>
			<ReportCardDescriptionTable.Body>
				{items.map(({ gradeValue, gradeLabel, boundaries, description }, i) => (
					<ReportCardDescriptionTable.Row key={i}>
						<ReportCardDescriptionTable.Cell column="grade">
							<Column justify="center" align="center" style={{ height: "100%" }}>
								<strong>{gradeValue}</strong>
								<span>{gradeLabel}</span>
							</Column>
						</ReportCardDescriptionTable.Cell>
						<ReportCardDescriptionTable.Cell column="boundaries">
							<Column justify="center" align="center" style={{ height: "100%" }}>
								{boundaries}
							</Column>
						</ReportCardDescriptionTable.Cell>
						<ReportCardDescriptionTable.Cell column="description">
							{description.map((text, i) => (
								<Paragraph key={i}>{text}</Paragraph>
							))}
						</ReportCardDescriptionTable.Cell>
					</ReportCardDescriptionTable.Row>
				))}
			</ReportCardDescriptionTable.Body>
		</ReportCardDescriptionTable>
	);
}
